/**
 * NumberPlateSearchBar Component
 *
 * Interactive editable German number plate search.
 * White plate with dark text, matching real German Kennzeichen style.
 * Fires a search 1 second after the user stops typing (rate-limited).
 *
 * Supports two modes:
 * - Standalone (default): Full container with search icon and active indicator
 * - Inline (`inline` prop): Compact mode for embedding in FilterBar row
 *
 * @module components/NumberPlateSearchBar
 */

import { FC, useState, useCallback, useRef, useEffect } from "react"
import {
  View,
  // eslint-disable-next-line no-restricted-imports
  TextInput,
  Pressable,
  ViewStyle,
  TextStyle,
  ActivityIndicator,
  Image,
  ImageStyle,
  Platform,
} from "react-native"
import Icon from "react-native-vector-icons/MaterialIcons"

import { colors, spacing } from "@/theme"
import { formatPartsForHubSpot } from "@/utils/plateUtils"

import { Text } from "./Text"

const DEBOUNCE_MS = 1000

// Character width at fontSize 14 in Kraftfahrzeugfont (monospace-ish plate font)
const CHAR_WIDTH = 11
const INPUT_PADDING = 12 // horizontal padding for inputs

interface NumberPlateSearchBarProps {
  /** Called with the normalized plate string when debounce fires or Enter is pressed */
  onSearch: (normalizedPlate: string) => void
  /** Called when the search is cleared */
  onClear: () => void
  /** Whether a plate search filter is currently active */
  isSearchActive: boolean
  /** Whether orders are currently loading */
  isLoading?: boolean
  /** Compact mode for embedding in FilterBar row (no outer container) */
  inline?: boolean
}

export const NumberPlateSearchBar: FC<NumberPlateSearchBarProps> = ({
  onSearch,
  onClear,
  isSearchActive,
  isLoading = false,
  inline = false,
}) => {
  const [city, setCity] = useState("")
  const [letters, setLetters] = useState("")
  const [numbers, setNumbers] = useState("")

  const cityRef = useRef<TextInput>(null)
  const lettersRef = useRef<TextInput>(null)
  const numbersRef = useRef<TextInput>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  /**
   * Combine parts and trigger search
   */
  const triggerSearch = useCallback(
    (c: string, l: string, n: string) => {
      const normalized = formatPartsForHubSpot(c.trim(), l.trim(), n.trim())
      if (normalized) {
        onSearch(normalized)
      } else if (isSearchActive) {
        onClear()
      }
    },
    [onSearch, onClear, isSearchActive],
  )

  /**
   * Schedule debounced search (1 second after last keystroke)
   */
  const scheduleDebouncedSearch = useCallback(
    (c: string, l: string, n: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        triggerSearch(c, l, n)
      }, DEBOUNCE_MS)
    },
    [triggerSearch],
  )

  /**
   * City code input handler (1-3 uppercase letters)
   */
  const handleCityChange = useCallback(
    (text: string) => {
      const cleaned = text
        .replace(/[^A-Z\u00C4\u00D6\u00DCa-z\u00E4\u00F6\u00FC]/g, "")
        .toUpperCase()
      setCity(cleaned)
      // Auto-advance when 3 chars or when user typed a space/hyphen (stripped)
      if (cleaned.length >= 3 || (text.length > cleaned.length && cleaned.length > 0)) {
        lettersRef.current?.focus()
      }
      scheduleDebouncedSearch(cleaned, letters, numbers)
    },
    [letters, numbers, scheduleDebouncedSearch],
  )

  /**
   * District letters input handler (0-2 uppercase letters)
   */
  const handleLettersChange = useCallback(
    (text: string) => {
      const cleaned = text
        .replace(/[^A-Z\u00C4\u00D6\u00DCa-z\u00E4\u00F6\u00FC]/g, "")
        .toUpperCase()
      setLetters(cleaned)
      // Auto-advance when 2 chars
      if (cleaned.length >= 2) {
        numbersRef.current?.focus()
      }
      scheduleDebouncedSearch(city, cleaned, numbers)
    },
    [city, numbers, scheduleDebouncedSearch],
  )

  /**
   * Numbers input handler (1-4 digits + optional E/H)
   */
  const handleNumbersChange = useCallback(
    (text: string) => {
      const cleaned = text.replace(/[^0-9EHeh]/g, "").toUpperCase()
      setNumbers(cleaned)
      scheduleDebouncedSearch(city, letters, cleaned)
    },
    [city, letters, scheduleDebouncedSearch],
  )

  /**
   * Handle Enter key - immediate search (bypass debounce)
   */
  const handleSubmit = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    triggerSearch(city, letters, numbers)
  }, [city, letters, numbers, triggerSearch])

  /**
   * Handle clear - reset all fields
   */
  const handleClear = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    setCity("")
    setLetters("")
    setNumbers("")
    onClear()
    cityRef.current?.focus()
  }, [onClear])

  /**
   * Handle backspace on empty field - navigate to previous field
   */
  const handleLettersKeyPress = useCallback(
    (e: any) => {
      if (e.nativeEvent.key === "Backspace" && letters.length === 0) {
        cityRef.current?.focus()
      }
    },
    [letters],
  )

  const handleNumbersKeyPress = useCallback(
    (e: any) => {
      if (e.nativeEvent.key === "Backspace" && numbers.length === 0) {
        lettersRef.current?.focus()
      }
    },
    [numbers],
  )

  const hasInput = city.length > 0 || letters.length > 0 || numbers.length > 0
  const showClear = hasInput || isSearchActive

  // Dynamic input widths based on content (default size = max chars for each field)
  const cityWidth = Math.max(city.length, 3) * CHAR_WIDTH + INPUT_PADDING
  const lettersWidth = Math.max(letters.length, 2) * CHAR_WIDTH + INPUT_PADDING
  const numbersWidth = Math.max(numbers.length, 4) * CHAR_WIDTH + INPUT_PADDING

  /**
   * Render the interactive plate visual
   */
  const renderPlate = () => (
    <View style={[$plateWrapper, isSearchActive && $plateWrapperActive]}>
      <View style={$plateContainer}>
        {/* Red Band */}
        <View style={$redBand}>
          <Image
            source={require("@assets/images/Logo white - transparent.png")}
            style={$redBandLogo}
            resizeMode="contain"
          />
        </View>

        {/* Input Area */}
        <View style={$inputArea}>
          {/* Part 1: City Code */}
          <TextInput
            ref={cityRef}
            style={[$plateInput, { width: cityWidth }]}
            value={city}
            onChangeText={handleCityChange}
            placeholder="STD"
            placeholderTextColor={colors.palette.neutral400}
            maxLength={3}
            autoCapitalize="characters"
            autoCorrect={false}
            returnKeyType="next"
            onSubmitEditing={() => lettersRef.current?.focus()}
          />

          {/* Seal and Dot */}
          <View style={$sealContainer}>
            <Image
              source={require("@assets/images/Plakette.png")}
              style={$sealImage}
              resizeMode="contain"
            />
            <View style={$sealDot} />
          </View>

          {/* Part 2: Letters */}
          <TextInput
            ref={lettersRef}
            style={[$plateInput, { width: lettersWidth }]}
            value={letters}
            onChangeText={handleLettersChange}
            placeholder="AB"
            placeholderTextColor={colors.palette.neutral400}
            maxLength={2}
            autoCapitalize="characters"
            autoCorrect={false}
            returnKeyType="next"
            onSubmitEditing={() => numbersRef.current?.focus()}
            onKeyPress={handleLettersKeyPress}
          />

          {/* Sticker Gap */}
          <View style={$stickerGap} />

          {/* Part 3: Numbers */}
          <TextInput
            ref={numbersRef}
            style={[$plateInput, { width: numbersWidth }]}
            value={numbers}
            onChangeText={handleNumbersChange}
            placeholder="1234"
            placeholderTextColor={colors.palette.neutral400}
            maxLength={5}
            autoCapitalize="characters"
            autoCorrect={false}
            returnKeyType="search"
            onSubmitEditing={handleSubmit}
            onKeyPress={handleNumbersKeyPress}
          />
        </View>
      </View>
    </View>
  )

  // Inline mode: just the plate + clear button in a compact row
  if (inline) {
    return (
      <View style={$inlineContainer}>
        <Icon name="search" size={18} color={isSearchActive ? colors.primary : colors.textDim} />
        {renderPlate()}
        {isLoading && <ActivityIndicator size="small" color={colors.primary} style={$loader} />}
        {showClear && !isLoading && (
          <Pressable onPress={handleClear} style={$clearButton} hitSlop={8}>
            <Icon name="close" size={16} color={colors.textDim} />
          </Pressable>
        )}
      </View>
    )
  }

  // Standalone mode: container with search icon and active indicator
  return (
    <View style={$container}>
      <View style={$searchRow}>
        <Icon name="search" size={20} color={isSearchActive ? colors.primary : colors.textDim} />
        {renderPlate()}
        {isLoading && <ActivityIndicator size="small" color={colors.primary} style={$loader} />}
        {showClear && !isLoading && (
          <Pressable onPress={handleClear} style={$clearButton} hitSlop={8}>
            <Icon name="close" size={18} color={colors.textDim} />
          </Pressable>
        )}
      </View>

      {isSearchActive && (
        <View style={$activeIndicator}>
          <Icon name="filter-list" size={14} color={colors.primary} />
          <Text style={$activeIndicatorText}>Gefiltert nach Kennzeichen</Text>
        </View>
      )}
    </View>
  )
}

// Styles

const $container: ViewStyle = {
  paddingHorizontal: spacing.xl,
  paddingVertical: spacing.sm,
}

const $searchRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.palette.neutral200,
  borderRadius: 12,
  paddingHorizontal: spacing.md,
  paddingVertical: Platform.OS === "web" ? 8 : spacing.xs,
  gap: spacing.sm,
}

const $inlineContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs,
  backgroundColor: colors.palette.neutral200,
  borderRadius: 8,
  paddingHorizontal: spacing.sm,
  paddingVertical: 4,
  borderWidth: 1,
  borderColor: colors.border,
}

// Plate visual styles

const $plateWrapper: ViewStyle = {
  // No fixed width - sizes from content
  height: 38,
  borderRadius: 6,
}

const $plateWrapperActive: ViewStyle = {
  // @ts-ignore - Web-specific
  boxShadow: "0 0 8px rgba(255, 56, 47, 0.4)",
}

const $plateContainer: ViewStyle = {
  height: "100%",
  backgroundColor: colors.surface,
  borderWidth: 1.5,
  borderColor: "rgba(228, 52, 43, 0.6)",
  borderRadius: 6,
  flexDirection: "row",
  alignItems: "stretch",
  overflow: "hidden",
}

const $redBand: ViewStyle = {
  alignItems: "center",
  backgroundColor: colors.primary,
  justifyContent: "center",
  width: 22,
}

const $redBandLogo: ImageStyle = {
  height: 18,
  width: 14,
}

const $inputArea: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  paddingHorizontal: 4,
  gap: 2,
}

const PLATE_FONT_FAMILY = "Kraftfahrzeugfont"
const PLATE_FONT_SIZE = 14

const $plateInput: TextStyle = {
  backgroundColor: colors.palette.neutral200,
  borderRadius: 3,
  color: colors.text,
  fontFamily: PLATE_FONT_FAMILY,
  fontSize: PLATE_FONT_SIZE,
  textAlign: "center",
  paddingVertical: 4,
  paddingHorizontal: 4,
  // @ts-ignore - Web-specific: remove browser outline
  outlineStyle: "none",
}

const $sealContainer: ViewStyle = {
  alignItems: "center",
  justifyContent: "space-around",
  width: 18,
  paddingVertical: 4,
  gap: 2,
}

const $sealImage: ImageStyle = {
  width: 10,
  height: 10,
}

const $sealDot: ViewStyle = {
  width: 10,
  height: 10,
  backgroundColor: colors.palette.neutral300,
  borderRadius: 10,
}

const $stickerGap: ViewStyle = {
  width: 8,
}

// Action button styles

const $loader: ViewStyle = {
  marginHorizontal: 2,
}

const $clearButton: ViewStyle = {
  padding: 4,
  borderRadius: 12,
}

const $activeIndicator: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xxs,
  marginTop: spacing.xs,
  paddingHorizontal: spacing.sm,
}

const $activeIndicatorText: TextStyle = {
  fontSize: 12,
  color: colors.primary,
  fontWeight: "500",
}
